import { CheckBoxIcon, MotionPlayIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Upload, Trash2, X } from 'lucide-react'

interface IntentsSelectionBarProps {
  selectedCount: number
  onExport: () => void
  onTrigger: () => void
  triggerDisabled?: boolean
  triggerDisabledReason?: string
  canTrigger: boolean
  onDelete: () => void
  canDelete: boolean
  onCancel: () => void
}

export function IntentsSelectionBar({
  selectedCount,
  onTrigger,
  triggerDisabled,
  triggerDisabledReason,
  canTrigger,
  onDelete,
  canDelete,
  onCancel,
  onExport,
}: IntentsSelectionBarProps) {
  if (selectedCount === 0) return null

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 rounded-lg border border-border-accent bg-background px-4 py-2 shadow-lg"
      role="region"
      aria-label="Selection actions"
      data-testid="intents-selection-bar"
    >
      <div className="flex items-center gap-2 pr-3 border-r">
        <CheckBoxIcon className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium" data-testid="intents-selection-count">
          {selectedCount} selected
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={onExport}
        data-testid="intents-selection-export"
      >
        <Upload className="h-4 w-4" />
        Export CSV
      </Button>
      {canTrigger && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={onTrigger}
                  disabled={triggerDisabled}
                  data-testid="intents-bulk-trigger"
                >
                  <MotionPlayIcon className="h-4 w-4" />
                  Trigger
                </Button>
              </span>
            </TooltipTrigger>
            {triggerDisabled && triggerDisabledReason && (
              <TooltipContent>{triggerDisabledReason}</TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}
      {canDelete && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-2 text-danger-foreground hover:text-danger-foreground hover:bg-danger"
          onClick={onDelete}
          data-testid="intents-bulk-delete"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={onCancel}
        data-testid="intents-selection-cancel"
      >
        <X className="h-4 w-4" />
        Cancel
      </Button>
    </div>
  )
}
